import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

const SIDEBAR_COLLAPSED_KEY = 'cockpit-chat-sidebar-collapsed';

interface ChatContextType {
  selectedChannelId: string | null;
  setSelectedChannelId: (id: string | null) => void;
  isChatOpen: boolean;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  totalUnreadCount: number;
  setTotalUnreadCount: (count: number) => void;
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  refreshKey: number;
  triggerChannelRefresh: () => void;
  activeThreadId: string | null;
  openThread: (messageId: string) => void;
  closeThread: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

function readCollapsedState(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readCollapsedState);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  const toggleChat = useCallback(() => {
    setIsChatOpen(prev => !prev);
  }, []);

  const openChat = useCallback(() => {
    setIsChatOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  const triggerChannelRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const openThread = useCallback((messageId: string) => {
    setActiveThreadId(messageId);
  }, []);

  const closeThread = useCallback(() => {
    setActiveThreadId(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        selectedChannelId,
        setSelectedChannelId,
        isChatOpen,
        toggleChat,
        openChat,
        closeChat,
        totalUnreadCount,
        setTotalUnreadCount,
        isSidebarCollapsed,
        toggleSidebar,
        refreshKey,
        triggerChannelRefresh,
        activeThreadId,
        openThread,
        closeThread,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
